import { useCurrentUser } from "@/hooks";
import { useCompleteSignOut } from "@/hooks/useCompleteSignOut";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const ACTIVITY_THROTTLE_MS = 1000;

const BUBBLE_ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "touchstart"] as const;
const CAPTURE_ACTIVITY_EVENTS = ["scroll", "wheel"] as const;

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation("toasts");

  const { data: currentUser } = useCurrentUser();
  const completeSignOut = useCompleteSignOut();

  const lastActivityRef = useRef(Date.now());
  const showingWarningRef = useRef(false);
  const [, setShowingWarning] = useState(false);

  const isLoggedIn = !!currentUser;

  const sessionLogout = useCallback(() => {
    void completeSignOut();
  }, [completeSignOut]);

  const dismissWarning = useCallback(() => {
    if (!showingWarningRef.current) return;
    showingWarningRef.current = false;
    setShowingWarning(false);
    toast.dismiss();
  }, []);

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    dismissWarning();
  }, [dismissWarning]);

  // Reset activity timer whenever the user becomes authenticated
  useEffect(() => {
    if (!isLoggedIn) return;
    lastActivityRef.current = Date.now();
    showingWarningRef.current = false;
    setShowingWarning(false);
  }, [isLoggedIn]);

  // Track user activity while logged in
  useEffect(() => {
    if (!isLoggedIn) return;

    let lastRecordedAt = 0;

    const activityHandler = () => {
      const now = Date.now();
      if (now - lastRecordedAt < ACTIVITY_THROTTLE_MS) return;
      lastRecordedAt = now;
      recordActivity();
    };

    BUBBLE_ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, activityHandler);
    });
    CAPTURE_ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, activityHandler, { capture: true, passive: true });
    });

    return () => {
      BUBBLE_ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, activityHandler);
      });
      CAPTURE_ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, activityHandler, { capture: true });
      });
    };
  }, [isLoggedIn, recordActivity]);

  // Inactivity timeout: warn at 18 min, log out at 20 min
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkActivity = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const twentyMinutes = 20 * 60 * 1000;
      const eighteenMinutes = 18 * 60 * 1000;

      if (elapsed >= eighteenMinutes && elapsed < twentyMinutes && !showingWarningRef.current) {
        toast.warning(t("inactivityWarning"), { autoClose: 10000, position: "top-right" });
        showingWarningRef.current = true;
        setShowingWarning(true);
      }

      if (elapsed >= twentyMinutes) {
        toast.error(t("inactivityLogout"), { autoClose: 5000, position: "top-center" });
        setTimeout(() => sessionLogout(), 1500);
        clearInterval(checkActivity);
      }
    }, 60000);

    return () => clearInterval(checkActivity);
  }, [isLoggedIn, sessionLogout, t]);

  return children;
};
